import { useState } from "react";
import { Link, NavLink } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, Youtube } from "lucide-react";
import RecordLogo from "@/components/brand/RecordLogo";
import { SHI_SHEET_URL, YOUTUBE_URL } from "@/lib/images";

const LINKS = [
  { to: "/shows", label: "Shows", testid: "nav-link-shows" },
  { to: "/shi-sips", label: "Shi Sips", testid: "nav-link-sips" },
  { to: "/music", label: "Music", testid: "nav-link-music" },
  { to: "/about", label: "About", testid: "nav-link-about" },
];

export default function Navbar() {
  const [open, setOpen] = useState(false);

  return (
    <header
      data-testid="site-header"
      className="fixed top-0 left-0 right-0 z-50 border-b border-corduroy/60 bg-ink/85 backdrop-blur-xl"
    >
      <div className="mx-auto flex h-16 max-w-7xl items-center justify-between px-5 md:h-20 md:px-10">
        <Link
          to="/"
          data-testid="nav-brand"
          className="group flex items-center gap-3"
          onClick={() => setOpen(false)}
        >
          {/* The circular master mark — a record, spinning almost subliminally */}
          <RecordLogo
            size={40}
            shadow={false}
            testid="nav-record-logo"
            className="ring-1 ring-gold/40"
          />
            <span className="leading-none">
            <span className="block font-display text-[13px] font-bold uppercase tracking-[0.16em] text-cream md:text-sm">
              That&rsquo;s What SHi Said
            </span>
          </span>
        </Link>

        <nav className="hidden items-center gap-7 lg:flex" aria-label="Primary">
          {LINKS.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              data-testid={l.testid}
              className={({ isActive }) =>
                `relative font-sans text-[11px] font-bold uppercase tracking-[0.22em] transition-colors hover:text-gold ${
                  isActive ? "text-gold" : "text-cream/75"
                }`
              }
            >
              {l.label}
            </NavLink>
          ))}
          <a
            href={YOUTUBE_URL}
            target="_blank"
            rel="noopener"
            data-testid="nav-watch-cta"
            className="inline-flex items-center gap-2 rounded-full border border-gold/60 px-5 py-2.5 font-sans text-[11px] font-bold uppercase tracking-[0.18em] text-gold transition-colors hover:bg-gold hover:text-ink"
          >
            <Youtube size={15} /> Watch
          </a>
          <a
            href={SHI_SHEET_URL}
            target="_blank"
            rel="noopener"
            data-testid="nav-shi-sheet-cta"
            className="rounded-full border border-gold bg-neon px-5 py-2.5 font-sans text-[11px] font-bold uppercase tracking-[0.18em] text-ink transition-colors hover:bg-cream"
          >
            Get The Shi Sheet
          </a>
        </nav>

        <button
          data-testid="nav-mobile-toggle"
          className="flex h-10 w-10 items-center justify-center rounded-full border border-corduroy text-cream lg:hidden"
          onClick={() => setOpen(!open)}
          aria-label={open ? "Close menu" : "Open menu"}
        >
          {open ? <X size={20} /> : <Menu size={20} />}
        </button>
      </div>

      <AnimatePresence>
        {open && (
          <motion.nav
            data-testid="nav-mobile-menu"
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
            className="overflow-hidden border-t border-corduroy/60 bg-ink/95 backdrop-blur-xl lg:hidden"
            aria-label="Mobile"
          >
            <div className="flex flex-col gap-1 px-5 py-6">
              {LINKS.map((l) => (
                <NavLink
                  key={l.to}
                  to={l.to}
                  data-testid={`nav-mobile-${l.testid}`}
                  onClick={() => setOpen(false)}
                  className="rounded-lg px-4 py-3 font-display text-2xl text-cream transition-colors hover:bg-cocoa hover:text-gold"
                >
                  {l.label}
                </NavLink>
              ))}
              <div className="mt-4 flex flex-col gap-3 border-t border-corduroy/60 pt-5">
                <a
                  href={YOUTUBE_URL}
                  target="_blank"
                  rel="noopener"
                  data-testid="nav-mobile-watch-cta"
                  onClick={() => setOpen(false)}
                  className="flex items-center justify-center gap-2 rounded-full border border-gold/60 px-6 py-3.5 text-center font-bold uppercase tracking-[0.18em] text-gold"
                >
                  <Youtube size={20} /> Watch
                </a>
                <a
                  href={SHI_SHEET_URL}
                  target="_blank"
                  rel="noopener"
                  data-testid="nav-mobile-shi-sheet-cta"
                  onClick={() => setOpen(false)}
                  className="rounded-full border border-gold bg-neon px-6 py-3.5 text-center font-bold uppercase tracking-[0.18em] text-ink"
                >
                  Get The Shi Sheet
                </a>
              </div>
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </header>
  );
}
